import type { ComponentType } from 'react';
import React, { useEffect, useState } from 'react';
import { store } from '../createStore';
import { getAsyncInjectors } from './asyncInjectors';

const { injectReducer, injectSagas } = getAsyncInjectors(store);

export interface ReducerDependency {
  type: 'reducer';
  reducerKey: string;
  importPromise: () => Promise<{ default: any }>;
}

export interface SagaDependency {
  type: 'saga';
  importPromise: () => Promise<{ default: any }>;
}

export type ModuleDependency = ReducerDependency | SagaDependency;

// Higher-order component that handles dynamic injection
export const withAsyncDependencies = <P extends object>(
  Component: ComponentType<P>,
  dependencies: ModuleDependency[] = []
) => {
  const AsyncComponent = (props: P) => {
    const [hasLoadedDependencies, setHasLoadedDependencies] = useState(false);

    useEffect(() => {
      if (hasLoadedDependencies) {
        return;
      }

      (async () => {
        for (const dependency of dependencies) {
          // eslint-disable-next-line no-await-in-loop
          const module = await dependency.importPromise();
          if (dependency.type === 'reducer') {
            injectReducer(dependency.reducerKey, module.default);
          } else if (dependency.type === 'saga') {
            injectSagas(module.default);
          }
        }

        setHasLoadedDependencies(true);
      })();
    }, [hasLoadedDependencies]);

    return hasLoadedDependencies ? <Component {...props} /> : null;
  };

  return AsyncComponent;
};

// Custom lazy loader that combines React.lazy with reducer/saga injection
export const asyncRouteLoader = (
  importPromise: () => Promise<{ default: ComponentType<any> }>,
  dependencies: ModuleDependency[] = []
) => {
  const LazyComponent = React.lazy(async () => {
    const module = await importPromise();

    // Create wrapped component with injected dependencies
    const WrappedComponent = withAsyncDependencies(
      module.default,
      dependencies
    );

    // Return object expected by React.lazy
    return { default: WrappedComponent };
  });

  return LazyComponent;
};
